<template>
  <BaseDialog
    :title="$t('common.startSurvey')"
    :is-open="isOpen"
    type="responsive"
    :align-dismiss-button-right="true"
    @close="emit('close-dialog')"
  >
    <LoadingWrapper :state="templateLoadingStatus">
      <ul class="flex flex-col">
        <template v-if="step === 'select-members-and-template'">
          <SurveyMembersForm
            v-model:feedback-sender-ids="surveyData.feedbackSenderIds"
            :label="$t('coaching.addCoachees')"
            :members-error="errors.get('feedbackSenderIds')"
            :show-select-all-button="true"
            member-filter="coachees"
          />
          <DialogBreak />
          <FeedbackTopicList
            v-model="surveyData.feedbackTopicId"
            :templates="filteredTemplates"
            :error="errors.get('feedbackTopicId')"
            :single-choice="true"
          />
        </template>
        <template v-else-if="step === 'select-question-groups'">
          <QuestionGroupSectionSelect
            v-model="surveyData.questionGroupIds"
            :templates="configurableTemplates"
            :error="errors.get('questionGroupIds')"
          />
        </template>
        <template v-else-if="step === 'add-metadata'">
          <BaseInputField
            v-model="surveyData.title"
            type="text"
            :label="$t('common.title')"
            class="bg-surface-container-high"
          />
          <DialogBreak />
          <h3 class="text-title-small">{{ $t('common.message') }} ({{ $t('common.optional') }})</h3>
          <WysiwygTextField
            v-model="surveyData.message"
            :placeholder="$t('survey.messagePlaceholder')"
          />
          <DialogBreak />
          <div class="flex flex-col gap-2">
            <SurveyStartDateForm
              v-model="surveyData.nextOccurrenceAt"
              :label="$t('survey.startDate')"
              class="rounded-large overflow-hidden"
            />
            <SurveyRecurrenceForm
              v-model:is-recurring="surveyData.isRecurring"
              v-model:recurrence-interval="surveyData.recurrenceInterval"
              v-model:recurrence-frequency="surveyData.recurrenceFrequency"
              v-model:start-date="surveyData.nextOccurrenceAt"
            />
          </div>
        </template>
      </ul>
    </LoadingWrapper>
    <template #confirm>
      <div class="flex flex-row gap-1">
        <BaseTextButton v-if="!isFirstStep" size="M" color="text" @click="previousStep">{{
          $t('common.back')
        }}</BaseTextButton>
        <BaseTextButton v-if="!isLastStep" size="M" color="filled" @click="nextStep">{{
          $t('common.continue')
        }}</BaseTextButton>
        <BaseTextButton v-if="isLastStep" size="M" color="filled" @click="saveSurvey">{{
          $t('common.start')
        }}</BaseTextButton>
      </div>
    </template>
    <template #dismiss>
      <BaseTextButton size="M" color="text" @click="emit('close-dialog')">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
  </BaseDialog>
</template>
<script setup lang="ts">
  import { toRef } from 'vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import WysiwygTextField from '@/core/components/base/wysiwyg/WysiwygTextField.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import FeedbackTopicList from '@/core/components/feedback-topic-list/FeedbackTopicList.vue'
  import { useStartSurvey } from '../composables/use-start-survey'
  import DialogBreak from '@/core/components/base/dialog/DialogBreak.vue'
  import SurveyMembersForm from './SurveyMembersForm.vue'
  import SurveyRecurrenceForm from './SurveyRecurrenceForm.vue'
  import SurveyStartDateForm from './SurveyStartDateForm.vue'
  import QuestionGroupSectionSelect from '@/core/components/question-group-section-select/QuestionGroupSectionSelect.vue'

  interface Props {
    isOpen: boolean
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'close-dialog'): void
    (e: 'survey-created', surveyId: string): void
  }>()
  const {
    step,
    isFirstStep,
    isLastStep,
    nextStep,
    previousStep,
    saveSurvey,
    surveyData,
    errors,
    filteredTemplates,
    templateLoadingStatus,
    configurableTemplates,
  } = useStartSurvey(toRef(props, 'isOpen'), (surveyId) => emit('survey-created', surveyId))
</script>
